import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { isObservable, Observable, Subscription } from "rxjs";
import { TranslateModule } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { MatMenuModule } from "@angular/material/menu";
import {
  AlternativeContentActionChildConfig,
  AlternativeContentActionConfig,
  AlternativeContentConfig,
} from "./alternative-content.model";
import { toAssetUrl } from "src/app/shared/helpers/asset-url.helper";
import { StoreFacadeService } from "src/app/pages/store-management/stores/stores-facade.service";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "app-alternative-content",
  standalone: true,
  imports: [CommonModule, TablerIconsModule, TranslateModule, MatMenuModule, MatTooltipModule],
  templateUrl: "./alternative-content.component.html",
  styleUrls: ["./alternative-content.component.scss"],
})
export class AlternativeContentComponent implements OnChanges, OnDestroy {
  private readonly storeFacade = inject(StoreFacadeService);

  @Input() config?: AlternativeContentConfig;
  @Output() actionClicked = new EventEmitter<AlternativeContentActionConfig>();

  actionDisabledStates: boolean[] = [];
  private stateSubscriptions = new Subscription();

  get resolvedImageSrc(): string | undefined {
    const imageSrc = this.config?.imageSrc;
    return imageSrc ? toAssetUrl(imageSrc) : undefined;
  }

  hasChildren(action: AlternativeContentActionConfig): boolean {
    return !!action.children?.length;
  }

  private validateActionsConfig(): void {
    if (!this.config?.actions) {
      return;
    }

    this.config.actions.forEach((action, index) => {
      const actionType = action.actionType ?? "button";

      // Validation: if dropdown, it must NOT have actionCallback
      if (actionType === "dropdown" && action.actionCallback) {
        throw new Error(
          `[AlternativeContent] Action at index ${index} with label "${action.label}" ` +
            `has actionType="dropdown" but also defines actionCallback. ` +
            `Dropdowns should NOT have actionCallback - only use children with their own callbacks.`,
        );
      }

      // Validation: if dropdown, it MUST have children
      if (actionType === "dropdown" && !action.children?.length) {
        throw new Error(
          `[AlternativeContent] Action at index ${index} with label "${action.label}" ` +
            `has actionType="dropdown" but no children are defined. ` +
            `Dropdowns must have at least one child action.`,
        );
      }

      // Validation: if button, it MUST have actionCallback
      if (actionType === "button" && !action.actionCallback) {
        throw new Error(
          `[AlternativeContent] Action at index ${index} with label "${action.label}" ` +
            `has actionType="button" but actionCallback is not defined. ` +
            `Buttons must have an actionCallback function.`,
        );
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["config"]) {
      this.validateActionsConfig();
      this.initializeActionStates();
    }
  }

  ngOnDestroy(): void {
    this.stateSubscriptions.unsubscribe();
  }

  onAction(action: AlternativeContentActionConfig): void {
    if (this.isActionDisabled(action)) {
      return;
    }

    if (action.actionCallback) {
      const result = action.actionCallback();
      if (result instanceof Promise) {
        result.catch((error) => {
          console.error("AlternativeContent action failed", error);
        });
      }
    }

    this.actionClicked.emit(action);
  }

  get actions(): AlternativeContentActionConfig[] {
    return this.config?.actions ?? [];
  }

  private initializeActionStates(): void {
    this.stateSubscriptions.unsubscribe();
    this.stateSubscriptions = new Subscription();
    this.actionDisabledStates = [];

    this.actions.forEach((action, index) => {
      const disabledValue = this.evaluateActionDisabled(action);
      if (isObservable(disabledValue)) {
        this.actionDisabledStates[index] = false;
        this.stateSubscriptions.add(
          disabledValue.subscribe({
            next: (value) => {
              this.actionDisabledStates[index] = value;
            },
            error: (error) => {
              console.error(
                "AlternativeContent action disable Observable failed",
                error,
              );
              this.actionDisabledStates[index] = true;
            },
          }),
        );
      } else {
        this.actionDisabledStates[index] = disabledValue;
      }
    });
  }

  isActionDisabled(
    action:
      | AlternativeContentActionConfig
      | AlternativeContentActionChildConfig,
  ): boolean {
    const index = this.actions.indexOf(
      action as AlternativeContentActionConfig,
    );
    if (index >= 0) {
      return this.actionDisabledStates[index] ?? false;
    }

    const disabledValue = this.evaluateActionDisabled(action);
    return isObservable(disabledValue) ? false : disabledValue;
  }

  private evaluateActionDisabled(
    action: AlternativeContentActionConfig,
  ): boolean | Observable<boolean> {
    if (typeof action.canExecute === "function") {
      try {
        const result = action.canExecute();
        return this.normalizeDisabledResult(result, true);
      } catch (error) {
        console.error("AlternativeContent canExecute callback failed", error);
        return true;
      }
    }

    if (typeof action.disableCallback === "function") {
      try {
        const result = action.disableCallback();
        return this.normalizeDisabledResult(result, false);
      } catch (error) {
        console.error(
          "AlternativeContent action disable callback failed",
          error,
        );
        return true;
      }
    }

    return false;
  }

  private normalizeDisabledResult(
    result: boolean | Observable<boolean>,
    inverted: boolean,
  ): boolean | Observable<boolean> {
    if (isObservable(result)) {
      return result;
    }
    return inverted ? !result : result;
  }

  getActionClasses(action: AlternativeContentActionConfig): string {
    const bg = action.bgBtn ?? "primary";
    return [`action-btn`, `action-btn-${bg}`].join(" ");
  }

  getProviderLogo(providerValue?: string): string {
    return this.storeFacade.getProviderLogo(providerValue);
  }

  isAmazonProvider(providerValue?: string): boolean {
    return !!providerValue && providerValue.toLowerCase().includes("amazon");
  }
}
